import React, { useState } from "react";
import { Alert, ToggleButton, ToggleButtonGroup } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CustomButton from "../../../../components/buttons/CustomButton";
import Toast from "../../../../components/support/Toast";
import ConfirmPopup from "../../../../popup/ConfirmPopup/ConfirmPopup";
import CranialCanvas from "./editor/CranialCanvas";
import { exportAnnotatedImage } from "../lib/annotatedImageExport";
import { formatConfidence, formatIndex, formatMm, formatPx } from "../lib/formatMeasurement";
import { classifyMeasurements } from "../lib/cranialClassification";
import { isCalibrationValid } from "../hooks/useCranialAnalysis";
import CranialSeverityChip from "../../../appointment/components/cranialAsymmetry/CranialSeverityChip";
import { aiWarningText } from "./AiAnalysisStep";
import { PROTOCOL_NOTE } from "./editor/editorColors";
import styles from "./CranialSteps.module.css";

const NO_SCALE_TEXT =
  "Referência física não identificada. As medidas lineares serão apresentadas em pixels; os índices percentuais continuam disponíveis.";

const LINEAR_ROWS = [
  { key: "length", label: "Comprimento (A-P)" },
  { key: "width", label: "Largura (orelha a orelha)" },
  { key: "diagonalA", label: "Diagonal A" },
  { key: "diagonalB", label: "Diagonal B" },
  { key: "cva", label: "CVA" },
];

const INDEX_ROWS = [
  { key: "cvai", label: "CVAI" },
  { key: "ci", label: "IC" },
];

const pad = (value) => String(value).padStart(2, "0");

const buildFileName = (date = new Date()) =>
  `analise-craniana-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}.jpg`;

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const calibrationText = (calibration, calibrated) => {
  if (!calibrated) return "Sem escala física";
  if (calibration.status === "manual") return "Escala definida manualmente";
  return "Escala por marcador identificado";
};

/**
 * Final screen: annotated or clean photo, measurement table, quality notes
 * and the annotated image download. Nothing is persisted.
 */
function CranialResultsPanel({ image, geometry, calibration, measurements, aiSuggestion, validation, onBackToEditor, onReset }) {
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmResetVisible, setConfirmResetVisible] = useState(false);
  const [toast, setToast] = useState(null);

  if (!image || !measurements) return null;

  const calibrated = isCalibrationValid(calibration) && Boolean(measurements.mm);
  const classification = classifyMeasurements(measurements);

  const handleDownload = () => {
    setIsExporting(true);
    exportAnnotatedImage({
      imageUrl: image.display.url,
      width: image.display.width,
      height: image.display.height,
      geometry,
      calibration,
      measurements,
    })
      .then((blob) => downloadBlob(blob, buildFileName()))
      .catch(() => setToast("Não foi possível gerar a imagem anotada. Tente novamente."))
      .finally(() => setIsExporting(false));
  };

  const qualityNotes = [
    ...((validation && validation.warnings) || []).map((warning) => warning.message),
    ...((aiSuggestion && aiSuggestion.warnings) || []).map(aiWarningText),
  ].filter(Boolean);

  return (
    <div className={styles.card}>
      <span className={styles.eyebrow}>Resultado</span>
      <h3 className={styles.cardTitle}>Medidas da análise</h3>

      {!measurements.ok ? (
        <Alert severity="warning" style={{ borderRadius: 12, marginTop: "1rem" }}>
          Não foi possível calcular as medidas com o traçado atual. Volte ao editor e revise o contorno e o eixo.
        </Alert>
      ) : (
        <div className={styles.resultsLayout} style={{ marginTop: "1rem" }}>
          <div className={styles.resultImage}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={showAnnotations ? "annotated" : "clean"}
              onChange={(event, value) => value && setShowAnnotations(value === "annotated")}
              style={{ marginBottom: "0.75rem" }}
            >
              <ToggleButton value="annotated" style={{ textTransform: "none", borderRadius: "12px 0 0 12px" }}>Anotada</ToggleButton>
              <ToggleButton value="clean" style={{ textTransform: "none", borderRadius: "0 12px 12px 0" }}>Foto limpa</ToggleButton>
            </ToggleButtonGroup>
            <CranialCanvas
              imageUrl={image.display.url}
              imageWidth={image.display.width}
              imageHeight={image.display.height}
              geometry={geometry}
              calibration={calibration}
              measurements={measurements}
              readOnly
              showAnnotations={showAnnotations}
            />
          </div>

          <div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Medida</th>
                  <th>Pixels</th>
                  {calibrated && <th>Escala real</th>}
                </tr>
              </thead>
              <tbody>
                {LINEAR_ROWS.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{formatPx(measurements.px[row.key])}</td>
                    {calibrated && <td>{formatMm(measurements.mm[row.key])}</td>}
                  </tr>
                ))}
                {INDEX_ROWS.map((row) => {
                  const rowClassification = classification ? classification[row.key] : null;
                  return (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td colSpan={calibrated ? 2 : 1}>
                        {rowClassification ? (
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <CranialSeverityChip
                              compact
                              value={formatIndex(measurements.indices[row.key])}
                              label={rowClassification.label}
                              color={rowClassification.color}
                              textColor={rowClassification.textColor}
                            />
                          </div>
                        ) : (
                          formatIndex(measurements.indices[row.key])
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {classification && (
              <div>
                <span className={styles.tag}>Leitura orientativa</span>
                <p className={styles.smallText}>
                  {`CVAI ${formatIndex(measurements.indices.cvai)}: ${classification.cvai.label} (escala de plagiocefalia)`}
                  <br />
                  {`IC ${formatIndex(measurements.indices.ci)}: ${classification.ci.label} (escala AP/BP)`}
                </p>
              </div>
            )}

            {!calibrated && (
              <Alert severity="info" style={{ borderRadius: 12, marginTop: "1rem" }}>
                {NO_SCALE_TEXT}
              </Alert>
            )}

            <div>
              <span className={styles.tag}>{calibrationText(calibration, calibrated)}</span>
              <span className={styles.tag}>
                {aiSuggestion
                  ? `Sugestão automática: confiança ${formatConfidence(aiSuggestion.confidence)}`
                  : "Traçado manual"}
              </span>
              {aiSuggestion && aiSuggestion.refinement && aiSuggestion.refinement.movedPoints > 0 && (
                <span className={styles.tag}>Contorno ajustado à borda da imagem</span>
              )}
              {aiSuggestion && geometry && geometry.wasManuallyEdited && (
                <span className={styles.tag}>Traçado revisado manualmente</span>
              )}
            </div>

            {qualityNotes.length > 0 && (
              <Alert severity="warning" style={{ borderRadius: 12, marginTop: "1rem" }}>
                <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                  {qualityNotes.map((text, index) => <li key={`${index}-${text}`}>{text}</li>)}
                </ul>
              </Alert>
            )}

            <p className={styles.smallText}>
              {`Classificação orientativa pelas mesmas escalas da ficha de assimetria craniana; não substitui avaliação profissional nem constitui diagnóstico. ${PROTOCOL_NOTE} Fórmula ${measurements.formula.name} v${measurements.formula.version}.`}
            </p>
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <CustomButton variant="outlined" style="primary" label="Nova análise" icon={<RestartAltIcon />} onClick={() => setConfirmResetVisible(true)} />
        <CustomButton variant="outlined" style="primary" label="Voltar ao editor" icon={<EditOutlinedIcon />} onClick={onBackToEditor} />
        {measurements.ok && (
          <CustomButton
            variant="contained"
            style="primary"
            label="Baixar imagem anotada"
            icon={<DownloadOutlinedIcon />}
            onClick={handleDownload}
            enabled={!isExporting}
            loading={isExporting}
          />
        )}
      </div>

      <ConfirmPopup
        Visibility={confirmResetVisible}
        Title="Iniciar nova análise"
        Description="A foto, o traçado e as medidas desta análise serão descartados. Deseja continuar?"
        ConfirmLabel="Nova análise"
        OnConfirm={onReset}
        OnHide={() => setConfirmResetVisible(false)}
      />

      <Toast open={Boolean(toast)} message={toast || ""} severity="error" onClose={() => setToast(null)} />
    </div>
  );
}

export default CranialResultsPanel;
